import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    clearPosterVandalism,
    deletePoster,
    getVandalizedPosters,
    updatePosterVandalism,
    type PosterAdminFilter,
    type UpdatePosterVandalismRequest,
    type VandalizedPosterRow
} from "../api/posters";
import { useCampaigns } from "../api/campaigns";
import { showError, showSuccess } from "../shared/toast";
import { PosterAdminFilters, PosterAdminTabs, defaultPosterFilter } from "./components/PosterAdminNav";
import {
    HANDLING_LABELS,
    dateText,
    isClosed,
    matchesHandling,
    type HandlingFilter
} from "./posterVandalismFilter";
import "./poster-admin.css";

/**
 * "clearCase" = markeringen var fel, affischen sitter kvar.
 * "deletePoster" = hela registreringen var fel och tas bort från kartan och statistiken.
 */
type RemovalMode = "clearCase" | "deletePoster";

export const PosterVandalismPage = (): JSX.Element => {
    const queryClient = useQueryClient();
    const [filter, setFilter] = useState<PosterAdminFilter>(defaultPosterFilter);
    const [handling, setHandling] = useState<HandlingFilter>("all");
    const [reportNumberDrafts, setReportNumberDrafts] = useState<Record<string, string>>({});
    const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});
    const [removalTarget, setRemovalTarget] = useState<VandalizedPosterRow | null>(null);

    const { data: campaigns = [] } = useCampaigns();
    const { data: rows = [], isLoading } = useQuery({
        queryKey: ["posters", "admin", "vandalized", filter],
        queryFn: () => getVandalizedPosters(filter),
        staleTime: 30_000
    });

    const mutation = useMutation({
        mutationFn: ({ id, body }: { id: string; body: UpdatePosterVandalismRequest }) =>
            updatePosterVandalism(id, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["posters", "admin"] });
            queryClient.invalidateQueries({ queryKey: ["posters", "statistics"] });
            showSuccess("Vandaliseringsärendet uppdaterat");
        },
        onError: () => showError("Kunde inte spara ändringen")
    });

    const removal = useMutation({
        mutationFn: ({ id, mode }: { id: string; mode: RemovalMode }) =>
            mode === "clearCase" ? clearPosterVandalism(id) : deletePoster(id),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ["posters", "admin"] });
            queryClient.invalidateQueries({ queryKey: ["posters", "statistics"] });
            showSuccess(
                variables.mode === "clearCase"
                    ? "Vandaliseringsmarkeringen är borttagen"
                    : "Affischen är raderad"
            );
            setRemovalTarget(null);
        },
        onError: () => showError("Kunde inte ta bort posten")
    });

    const visibleRows = useMemo(
        () => rows.filter((row) => matchesHandling(row, handling)),
        [rows, handling]
    );

    const counts = useMemo(
        () => ({
            total: rows.length,
            quantity: rows.reduce((sum, row) => sum + row.quantity, 0),
            reported: rows.filter((row) => row.policeReportedAt !== null).length,
            open: rows.filter((row) => !isClosed(row)).length,
            repaired: rows.filter((row) => row.repairedAt !== null).length
        }),
        [rows]
    );

    const save = (id: string, body: UpdatePosterVandalismRequest) => mutation.mutate({ id, body });

    const exportCsv = () => {
        const headers = [
            "Adress",
            "Kampanj",
            "Latitud",
            "Longitud",
            "Antal",
            "Uppsatt av",
            "Uppsatt",
            "Vandaliserad",
            "Polisanmäld",
            "Diarienummer",
            "Nedtagen",
            "Lagad",
            "Anteckning"
        ];
        const csvRows = visibleRows.map((row) => [
            row.addressLabel ?? "",
            row.campaignName,
            row.latitude,
            row.longitude,
            row.quantity,
            row.placedByName,
            dateText(row.placedAt),
            dateText(row.vandalizedAt),
            dateText(row.policeReportedAt),
            row.policeReportNumber ?? "",
            dateText(row.takenDownAt),
            dateText(row.repairedAt),
            (row.vandalismNotes ?? "").replace(/"/g, "'")
        ]);
        const csv = [headers, ...csvRows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
        const link = document.createElement("a");
        link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
        link.download = `vandaliserade-affischer-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
    };

    return (
        <section className="poster-admin" aria-label="Vandaliserade affischer">
            <div className="poster-admin__header">
                <h2>Affischering</h2>
                <button
                    type="button"
                    className="poster-admin__button"
                    onClick={exportCsv}
                    disabled={visibleRows.length === 0}
                >
                    Exportera CSV
                </button>
            </div>

            <PosterAdminTabs />

            <PosterAdminFilters
                filter={filter}
                onChange={setFilter}
                campaigns={campaigns}
                extra={
                    <div className="filter-group">
                        <label htmlFor="poster-handling">Handläggning:</label>
                        <select
                            id="poster-handling"
                            className="filter-select"
                            value={handling}
                            onChange={(e) => setHandling(e.target.value as HandlingFilter)}
                        >
                            {(Object.keys(HANDLING_LABELS) as HandlingFilter[]).map((key) => (
                                <option key={key} value={key}>
                                    {HANDLING_LABELS[key]}
                                </option>
                            ))}
                        </select>
                    </div>
                }
            />

            <div className="poster-admin__summary">
                <div className="summary-card">
                    <p className="summary-card__label">Vandaliseringar</p>
                    <p className="summary-card__value">{counts.total}</p>
                </div>
                <div className="summary-card">
                    <p className="summary-card__label">Affischer</p>
                    <p className="summary-card__value">{counts.quantity}</p>
                </div>
                <div className="summary-card">
                    <p className="summary-card__label">Polisanmälda</p>
                    <p className="summary-card__value">{counts.reported}</p>
                </div>
                <div className="summary-card">
                    <p className="summary-card__label">Ej åtgärdade</p>
                    <p className="summary-card__value">{counts.open}</p>
                </div>
                <div className="summary-card">
                    <p className="summary-card__label">Lagade</p>
                    <p className="summary-card__value">{counts.repaired}</p>
                </div>
            </div>

            {isLoading && <p className="poster-admin__empty">Hämtar vandaliserade affischer…</p>}

            <div className="poster-admin__table-container">
                <table className="poster-admin__table poster-admin__table--vandalism">
                    <thead>
                        <tr>
                            <th>Plats</th>
                            <th>Kampanj</th>
                            <th>Uppsatt</th>
                            <th>Vandaliserad</th>
                            <th>Polisanmäld</th>
                            <th>Nedtagen</th>
                            <th>Lagad</th>
                            <th>Anteckning</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {visibleRows.length === 0 && !isLoading && (
                            <tr>
                                <td colSpan={9} className="poster-admin__empty">
                                    Inga vandaliserade affischer i urvalet.
                                </td>
                            </tr>
                        )}
                        {visibleRows.map((row) => {
                            const coords = `${row.latitude.toFixed(5)}, ${row.longitude.toFixed(5)}`;
                            return (
                                <tr key={row.id}>
                                    <td>
                                        <div className="poster-admin__address">
                                            {row.addressLabel ?? "Adress saknas"}
                                        </div>
                                        <a
                                            className="poster-admin__coords"
                                            href={`https://www.google.com/maps?q=${row.latitude},${row.longitude}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Öppna platsen i Google Maps"
                                        >
                                            {coords}
                                        </a>
                                        <div className="poster-admin__muted">{row.quantity} st affischer</div>
                                    </td>
                                    <td>{row.campaignName}</td>
                                    <td>
                                        <div>{dateText(row.placedAt)}</div>
                                        <div className="poster-admin__muted">{row.placedByName}</div>
                                    </td>
                                    <td>
                                        <div>{dateText(row.vandalizedAt)}</div>
                                        {row.vandalizedByName && (
                                            <div className="poster-admin__muted">{row.vandalizedByName}</div>
                                        )}
                                    </td>
                                    <td>
                                        <label className="poster-admin__check">
                                            <input
                                                type="checkbox"
                                                checked={row.policeReportedAt !== null}
                                                disabled={mutation.isPending}
                                                onChange={(e) =>
                                                    save(row.id, { policeReported: e.target.checked })
                                                }
                                            />
                                            {row.policeReportedAt ? dateText(row.policeReportedAt) : "Anmäl"}
                                        </label>
                                        <input
                                            type="text"
                                            className="poster-admin__input"
                                            placeholder="Diarienummer"
                                            value={
                                                reportNumberDrafts[row.id] ?? row.policeReportNumber ?? ""
                                            }
                                            onChange={(e) =>
                                                setReportNumberDrafts((prev) => ({
                                                    ...prev,
                                                    [row.id]: e.target.value
                                                }))
                                            }
                                            onBlur={(e) => {
                                                const value = e.target.value.trim();
                                                if (value === (row.policeReportNumber ?? "")) return;
                                                save(row.id, { policeReportNumber: value });
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <label className="poster-admin__check">
                                            <input
                                                type="checkbox"
                                                checked={row.takenDownAt !== null}
                                                disabled={mutation.isPending}
                                                onChange={(e) => save(row.id, { takenDown: e.target.checked })}
                                            />
                                            {row.takenDownAt ? dateText(row.takenDownAt) : "Nedtagen"}
                                        </label>
                                    </td>
                                    <td>
                                        <label className="poster-admin__check">
                                            <input
                                                type="checkbox"
                                                checked={row.repairedAt !== null}
                                                disabled={mutation.isPending}
                                                onChange={(e) => save(row.id, { repaired: e.target.checked })}
                                            />
                                            {row.repairedAt ? dateText(row.repairedAt) : "Lagad"}
                                        </label>
                                    </td>
                                    <td>
                                        <textarea
                                            className="poster-admin__notes"
                                            rows={2}
                                            placeholder="Anteckning för polisanmälan…"
                                            value={noteDrafts[row.id] ?? row.vandalismNotes ?? ""}
                                            onChange={(e) =>
                                                setNoteDrafts((prev) => ({ ...prev, [row.id]: e.target.value }))
                                            }
                                            onBlur={(e) => {
                                                const value = e.target.value.trim();
                                                if (value === (row.vandalismNotes ?? "")) return;
                                                save(row.id, { vandalismNotes: value });
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="poster-admin__remove"
                                            onClick={() => setRemovalTarget(row)}
                                            title="Ta bort felaktig markering eller radera affischen"
                                        >
                                            Ta bort
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {removalTarget && (
                <div
                    className="poster-admin__dialog-backdrop"
                    role="presentation"
                    onClick={() => !removal.isPending && setRemovalTarget(null)}
                >
                    <div
                        className="poster-admin__dialog"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="poster-removal-title"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 id="poster-removal-title">Ta bort</h3>
                        <p className="poster-admin__dialog-text">
                            {removalTarget.addressLabel ?? "Affisch utan adress"} ·{" "}
                            {removalTarget.quantity} st · uppsatt av {removalTarget.placedByName}
                        </p>
                        <div className="poster-admin__dialog-actions">
                            <button
                                type="button"
                                className="poster-admin__button"
                                disabled={removal.isPending}
                                onClick={() =>
                                    removal.mutate({ id: removalTarget.id, mode: "clearCase" })
                                }
                            >
                                Var inte vandaliserad
                                <span className="poster-admin__muted">
                                    Markeringen tas bort, affischen sitter kvar
                                </span>
                            </button>
                            <button
                                type="button"
                                className="poster-admin__button poster-admin__button--danger"
                                disabled={removal.isPending}
                                onClick={() =>
                                    removal.mutate({ id: removalTarget.id, mode: "deletePoster" })
                                }
                            >
                                Radera affischen
                                <span className="poster-admin__muted">
                                    Hela registreringen försvinner från karta och statistik
                                </span>
                            </button>
                            <button
                                type="button"
                                className="filter-clear-button"
                                disabled={removal.isPending}
                                onClick={() => setRemovalTarget(null)}
                            >
                                Avbryt
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};
